/**
 * Glidejs slider responsive view module
 * Glidejs accepts functions as components ONLY!
 * On default Glide-, Components- and Events-object are passed to a custom glidejs-component
 * @see https://glidejs.com/docs/extending-components/
 * @param {object} Glide
 * @param {object} Components
 * @param {object} Events
 * @constructor
 */
const ResponsiveView = (Glide, Components, Events) => {
  const config = {
    elements: {
      controls: '[data-slider-controls]',
      track: '[data-glide-el="track"]',
      slide: '[data-slider-slide]',
      slideWrapper: '[data-slider-slide-wrapper]',
    },
    modifier: {
      hidden: 'content-slider__controls-container--hidden',
      justifyTrack: 'content-slider__track--justify',
      justifySlide: 'content-slide__wrapper--justify',
      alignLeft: 'content-slide__wrapper--align-left',
      alignRight: 'content-slide__wrapper--align-right',
      hiddenPeek: 'content-slider--hidden-peek',
    },
  };

  /**
   * Compares total amount of slides with slides allowed to show "perView"
   * and hides/shows slider controls
   * @type {{mount(): void}}
   */
  const ResponsiveControls = {
    /**
     * global hook for glidejs slider mount.
     * fired when slider is mounted and get the total slide number,
     * set bullet for each slide and bind event for each bullet
     */
    mount() {
      const totalCount = Components.Sizes.length;
      const optionCount = Glide.settings.perView;
      this.controlsContainer = Glide.selector.querySelector(config.elements.controls);
      if (totalCount <= optionCount) {
        this.hideControls();
      }
      if (totalCount === optionCount) {
        this.removePeek();
      }
    },

    removePeek() {
      Glide.selector.classList.add(config.modifier.hiddenPeek);
      Glide.selector.querySelector(config.elements.track)
        .classList.add(config.modifier.justifyTrack);
      Glide.selector.querySelectorAll(config.elements.slide)
        .forEach((slide, index, parent) => {
          slide.querySelector(config.elements.slideWrapper)
            .classList.add(config.modifier.justifySlide);
          if (index === 0) {
            slide.querySelector(config.elements.slideWrapper)
              .classList.add(config.modifier.alignLeft);
          }
          if (index === parent.length - 1) {
            slide.querySelector(config.elements.slideWrapper)
              .classList.add(config.modifier.alignRight);
          }
        });
      Glide.update({ peek: 0 });
    },

    addPeek() {
      Glide.selector.classList.remove(config.modifier.hiddenPeek);
      Glide.selector.querySelector(config.elements.track)
        .classList.remove(config.modifier.justifyTrack);
      Glide.selector.querySelectorAll(config.elements.slide)
        .forEach((slide, index, parent) => {
          slide.querySelector(config.elements.slideWrapper)
            .classList.remove(config.modifier.justifySlide);
          if (index === 0) {
            slide.querySelector(config.elements.slideWrapper)
              .classList.remove(config.modifier.alignLeft);
          }
          if (index === parent.length - 1) {
            slide.querySelector(config.elements.slideWrapper)
              .classList.remove(config.modifier.alignRight);
          }
        });
      Glide.update({ peek: Glide.settings.peek });
    },

    /**
     * Hides Controls
     */
    hideControls() {
      this.controlsContainer.classList.add(config.modifier.hidden);
    },

    /**
     * Shows Controls
     */
    showControls() {
      this.controlsContainer.classList.remove(config.modifier.hidden);
    },
  };

  /**
   * fired on glide-event "resize"
   * "resize" is fired when viewport is changed
   * and the responsive options of the slider is triggered.
   * @see https://glidejs.com/docs/events/
   */
  Events.on('resize', () => {
    const totalCount = Components.Sizes.length;
    const optionCount = Glide.settings.perView;

    if (totalCount <= optionCount) {
      ResponsiveControls.hideControls();
    } else {
      ResponsiveControls.showControls();
    }

    if (totalCount === optionCount) {
      ResponsiveControls.removePeek();
    } else {
      ResponsiveControls.addPeek(optionCount);
    }
  });

  return ResponsiveControls;
};

export default ResponsiveView;
